import { Component, computed, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Language, LanguageOption, LanguageService } from '../../../core/services/language.service';

@Component({
  selector: 'app-language-switcher',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule, ButtonModule, TooltipModule],
  template: `
    @if (mode === 'dropdown') {
      <p-select
        [options]="languages"
        [(ngModel)]="currentLanguage"
        (onChange)="onLanguageChange($event.value)"
        optionLabel="nativeName"
        optionValue="code"
        [style]="{ 'min-width': '140px' }"
        class="language-select"
      >
        <ng-template let-option pTemplate="item">
          <div class="flex align-items-center gap-2">
            <span>{{ option.flag }}</span>
            <span>{{ option.nativeName }}</span>
          </div>
        </ng-template>
        <ng-template let-option pTemplate="selectedItem">
          <div class="flex align-items-center gap-2" *ngIf="option">
            <span>{{ option.flag }}</span>
            <span>{{ option.nativeName }}</span>
          </div>
        </ng-template>
      </p-select>
    }

    @if (mode === 'toggle') {
      <p-button
        [label]="otherLanguage().nativeName"
        [icon]="'pi pi-globe'"
        (onClick)="toggleLanguage()"
        [outlined]="true"
        severity="secondary"
        [style]="{ 'min-width': '120px' }"
      />
    }

    @if (mode === 'icon') {
      <p-button
        [icon]="'pi pi-globe'"
        (onClick)="toggleLanguage()"
        [text]="true"
        [rounded]="true"
        severity="secondary"
        [pTooltip]="'Switch to ' + otherLanguage().nativeName"
        tooltipPosition="bottom"
      />
    }
  `,
  styles: [`
    :host {
      display: inline-block;
    }

    .language-select {
      ::ng-deep {
        .p-select-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
      }
    }
  `]
})
export class LanguageSwitcherComponent {
  @Input() mode: 'dropdown' | 'toggle' | 'icon' = 'dropdown';

  languageService = inject(LanguageService);

  languages = this.languageService.languages;
  currentLanguage = this.languageService.currentLanguage();

  // Computed signal for the other language
  otherLanguage = computed(() => {
    const current = this.languageService.currentLanguage();
    return this.languages.find(l => l.code !== current) || this.languages[0];
  });

  onLanguageChange(lang: Language): void {
    this.languageService.setLanguage(lang);
    this.currentLanguage = lang;
  }

  toggleLanguage(): void {
    this.languageService.toggleLanguage();
    this.currentLanguage = this.languageService.currentLanguage();
  }
}
